import { FaceDetection } from '@vladmandic/face-api';
import { useEffect, useRef, useState } from 'react';

import { FaceApiClient } from 'lib/clients/FaceApiClient';
import FrameTriggerClient from 'lib/clients/FrameTriggerClient';
import LivePhotoCaptureClient from 'lib/clients/LivePhotoCaptureClient';
import { GovernmentIdSide } from 'lib/interfaces/verificationConfig';
import { CameraInspectionData } from 'verify/lib/interfaces/cameraInspection';
import { bufferHorizontalImage } from 'verify/lib/utils/webcam';
import { CaptureMethod, ClientExtraction } from 'verify/models/verification';
import {
  BARCODE_TRIGGER_CONFIGS,
  BarcodeTriggerParams,
  BarcodeTriggerType,
  DETECT_INTERVAL,
} from 'verify/scanners/BarcodeScanner/client';
import { parseAamva, scanForPdf417 } from 'verify/scanners/BarcodeScanner/parsing';
import BaseScanner from 'verify/scanners/BaseScanner';

const DELAY_UNTIL_MANUAL_CAPTURE = 8000;
const FACE_DETECTION_THRESHOLD = 0.3;

interface BarcodeScannerProps {
  delayUntilManualCaptureMs?: number;
  handleCapture: (
    imageDataUris: string[],
    captureMethod: CaptureMethod,
    cameraInspectionData?: CameraInspectionData,
    clientExtractions?: ClientExtraction[],
  ) => Promise<void>;
  handleError: (err: Error | string, state: 'load' | 'capture') => void;
  labelTakePhoto: string;
  overlay: JSX.Element;
  requiresSide?: GovernmentIdSide;
  zoomFactor: number;
  videoConstraints: MediaTrackConstraints;
}

const BarcodeScanner = ({
  delayUntilManualCaptureMs = DELAY_UNTIL_MANUAL_CAPTURE,
  handleCapture,
  handleError,
  labelTakePhoto,
  overlay,
  requiresSide,
  zoomFactor,
  videoConstraints,
}: BarcodeScannerProps) => {
  useEffect(() => {
    FaceApiClient.loadModels();
  }, []);

  const [errored, setErrored] = useState(false);
  const refCapturedSet = useRef([]);

  const paramsFromVideoFrame = async (
    videoElement: HTMLVideoElement,
    canvasBuffer: HTMLCanvasElement,
    captureClient: LivePhotoCaptureClient,
    _triggerClient: FrameTriggerClient<BarcodeTriggerType, BarcodeTriggerParams>,
  ): Promise<BarcodeTriggerParams> => {
    bufferHorizontalImage({ source: videoElement, precreatedCanvas: canvasBuffer, zoomFactor });
    const decodedResult = await scanForPdf417(canvasBuffer, [0]);
    let successfullyScanned = false;

    // ZXing decoding often gives corrupted results so we wait until we see two repeats
    if (decodedResult.decodedResult && decodedResult.scannedImage) {
      const parsedResult = parseAamva(decodedResult.decodedResult);
      if (
        parsedResult.firstName &&
        parsedResult.lastName &&
        parsedResult.middleName &&
        parsedResult.dateOfBirth
      ) {
        const newCapturedSet = JSON.stringify({
          firstName: parsedResult.firstName,
          lastName: parsedResult.lastName,
          middleName: parsedResult.middleName,
          dateOfBirth: parsedResult.dateOfBirth,
        });

        // Capture screenshot if parsed barcode is consistent with a previous result.
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        successfullyScanned = refCapturedSet.current.includes(newCapturedSet);
        if (successfullyScanned) {
          // Manually add contents from capture
          captureClient?.manualCapture(decodedResult.scannedImage);
        } else {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          refCapturedSet.current = [...refCapturedSet.current, newCapturedSet];
        }
      }
    }

    let detection: FaceDetection[] = [];
    if (!errored && !successfullyScanned) {
      try {
        detection = await FaceApiClient.detectFaces(canvasBuffer, {
          scoreThreshold: FACE_DETECTION_THRESHOLD,
        });
      } catch (err) {
        // NOTE: Some Android devices fail to compile the fragment shader.
        // If many devices hit this, consider following recommendation here:
        // https://github.com/tensorflow/tfjs/issues/952

        // Here, we stop tick'ing but do nothing else because they can manually take a photo.
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        handleError(err, 'capture');
        setErrored(true);
      }
    }

    let faceBoundingBox;
    if (detection.length === 1) {
      // Normalize bounding box
      faceBoundingBox = detection[0].box.rescale({
        width: 1 / canvasBuffer.width,
        height: 1 / canvasBuffer.height,
      });
    }

    return {
      successfullyScanned,
      detectedGuards: decodedResult.detectedGuards,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      requiresSide: requiresSide,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      faceBoundingBox,
    };
  };
  const shouldSubmit = (
    params: BarcodeTriggerParams,
    triggerClient: FrameTriggerClient<BarcodeTriggerType, BarcodeTriggerParams>,
  ) => {
    return (
      // ZXing is unreliable, so count two repeat scans as successful
      params.successfullyScanned ||
      // Even without a successful scan, we'll capture after a delay to improve the experience
      triggerClient.getTriggered().getType() === BarcodeTriggerType.Capture
    );
  };

  return (
    <BaseScanner<BarcodeTriggerType, BarcodeTriggerParams>
      handleCapture={({ imageSrcs, captureMethod }) => handleCapture(imageSrcs, captureMethod)}
      handleFailed={handleError}
      detectInterval={DETECT_INTERVAL}
      labelTakePhoto={labelTakePhoto}
      videoConstraints={videoConstraints}
      triggerConfigs={BARCODE_TRIGGER_CONFIGS}
      paramsFromVideoFrame={paramsFromVideoFrame}
      shouldSubmit={shouldSubmit}
      overlay={overlay}
      delayUntilManualCaptureMs={delayUntilManualCaptureMs}
      zoomFactor={zoomFactor}
      scannerType='barcode'
    />
  );
};

export default BarcodeScanner;
